import type { ButtonHTMLAttributes, ReactNode } from "react";

interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "type"
> {
  isLoading?: boolean;
  children?: ReactNode;
  text?: string;
  loadingText?: string;
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "secondary" | "danger";
  fullWidth?: boolean;
  size?: "sm" | "md" | "lg";
}

export const Button = ({
  isLoading = false,
  children,
  text,
  loadingText,
  type = "button",
  variant = "primary",
  fullWidth = false,
  size = "md",
  className = "",
  disabled,
  ...props
}: ButtonProps) => {
  const displayText = text ?? children;
  const displayLoadingText = loadingText ?? displayText;

  // Clases de tamaño
  const sizeClasses = {
    sm: "btn-compact-sm",
    md: "btn-compact",
    lg: "btn-compact-lg",
  };

  const baseClasses = `${sizeClasses[size]} font-semibold uppercase tracking-wider active:scale-[0.98] shadow-global-button disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`;

  // Clases de variante - solo primary está definida en el sistema de estilos
  // Para otras variantes, usar className personalizado
  const variantClasses: Record<string, string> = {
    primary: "btn-global-primary",
    secondary: "btn-global-secondary",
    danger: "btn-global-primary",
  };

  const widthClasses = fullWidth ? "w-full" : "";

  const commonClasses = fullWidth ? "mt-auto" : "";

  const finalClassName =
    `${baseClasses} ${variantClasses[variant]} ${widthClasses} ${commonClasses} ${className}`.trim();

  return (
    <button
      type={type}
      disabled={disabled || isLoading}
      className={finalClassName}
      {...props}
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2">
          <svg
            className="animate-spin h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>{displayLoadingText}</span>
        </div>
      ) : (
        displayText
      )}
    </button>
  );
};
